"use client";
import {
  Box,
  Flex,
  GridItem,
  SimpleGrid,
  Text,
  Button,
  Skeleton,
  Container,
} from "@chakra-ui/react";
import Link from "next/link";
import useSWR from "swr";
import { PostsService } from "@/_services/graphql/query/posts";
import { BlogPostCard } from "@/app/[locale]/(sidebar-layout)/discover/blog/_components/BlogPostCard";
import CustomEmptyState from "@/_components/ui/EmptyState/EmptyState";
import { useLocale, useTranslations } from "next-intl";

const postsService = new PostsService();

const BlogPostsSkeleton = () => (
  <Flex py="4rem" px="10rem" direction="column" align="center">
    <Skeleton height="3rem" width="20rem" mb="4rem" />
    <SimpleGrid
      columns={{ base: 1, md: 3 }}
      gap={{ base: 10, md: 6, lg: 20 }}
      w="full"
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <GridItem key={index}>
          <Skeleton height="12rem" borderRadius="2xl" />
        </GridItem>
      ))}
    </SimpleGrid>
  </Flex>
);

export const BlogPosts = () => {
  const locale = useLocale();
  const {
    data: postsData,
    isLoading,
    error,
  } = useSWR(
    "/blog/latest-posts",
    () => postsService.getLatestPost(6, locale),
    {
      revalidateOnFocus: false,
    },
  );
  const t = useTranslations("landing.blog");

  if (isLoading) {
    return <BlogPostsSkeleton />;
  }

  if (error || !postsData?.data?.allPost?.length) {
    return (
      <Flex py="4rem" px="10rem" direction="column" align="center">
        <Text
          textStyle="3xl"
          fontWeight="medium"
          mb="4rem"
          letterSpacing="-0.05rem"
          fontFamily="beVietnamPro"
          textAlign="center"
        >
          {t("title")}
        </Text>
        <CustomEmptyState
          title={t("no_posts")}
          description={t("empty_description")}
          minH="200px"
          placeholderImage="page-not-found-2"
        />
      </Flex>
    );
  }

  return (
    <Container maxW={{ base: "full", md: "1400px" }} px={0}>
      <Flex py="4rem" direction="column" align="center">
        <Text
          textStyle={{ base: "3xl", md: "4xl" }}
          fontWeight="semibold"
          letterSpacing="-0.05rem"
          fontFamily="heading"
          mb="4rem"
          textAlign="center"
          color="neutral.900"
        >
          {t("title")}
        </Text>
        <SimpleGrid
          w="full"
          columns={{ base: 1, md: 3 }}
          gap={{ base: 10, md: 6, lg: 10 }}
        >
          {postsData.data.allPost.map((post, index) => (
            <GridItem key={post._id || index} w="full">
              <BlogPostCard post={post} h="16rem" />
            </GridItem>
          ))}
        </SimpleGrid>

        <Box mt={12}>
          <Link href="/blog">
            <Button size="md" visual="outline" color="neutral.900">
              {t("read_more")}
            </Button>
          </Link>
        </Box>
      </Flex>
    </Container>
  );
};
